import { SubSection as ISubSection, SubSectionItem } from '@teko/discovery/v2';
import {
  Button,
  Flex,
  Glide,
  LazyImage,
  Shape,
  SubTitle,
  styled,
} from '@teko/ds-desktop';
import { TekoLink } from '@teko/teko-ecommerce';
import { getTrackingData } from '@teko/tracking';
import { useTenantConfig } from 'hooks';

const LIMIT_SUB_SECTION_BRANDS = 16;

export const SubSection = ({
  subsections,
  newLayout = false,
}: {
  subsections: Array<ISubSection>;
  newLayout?: boolean;
}) => {
  const { isMobile } = useTenantConfig();

  const isNewLayoutDesktop = newLayout && !isMobile;
  const isNewLayoutMobile = newLayout && isMobile;

  const itemGap = isNewLayoutMobile ? 's12' : 's8';

  const brands = subsections.filter(({ type }) => type === 'IMAGES');
  const others = subsections.filter(({ type }) => type === 'IMAGES_AND_TEXT');

  const renderSuggestionBrands = () => {
    if (
      !brands ||
      brands.length === 0 ||
      brands.every((brand) => !brand.items?.length)
    )
      return null;
    return (
      <>
        {brands.map((block, index) => {
          if (!block.items?.length) {
            return null;
          }
          return (
            <StyledFlex
              gap={itemGap}
              flexWrap={isMobile ? 'inherit' : 'wrap'}
              paddingTop="s8"
              marginBottom={!others || others.length === 0 ? 's4' : 's12'}
              style={isMobile ? { overflow: 'scroll' } : {}}
              key={`${index}`}
            >
              {block.items
                .slice(0, LIMIT_SUB_SECTION_BRANDS)
                .map((brand, index) =>
                  !!brand.thumbnailUrl && !!brand.linkUrl ? (
                    <TekoLink
                      key={`${index}${brand.linkUrl}`}
                      href={brand.linkUrl}
                    >
                      <Button
                        padding="s4"
                        width={110}
                        variant="default"
                        {...getTrackingData({
                          isTrackContent: true,
                          regionName: 'subSection',
                          contentName: JSON.stringify({
                            block: 'brand',
                            item: brand.linkUrl,
                          }),
                          target: brand.linkUrl,
                        })}
                      >
                        <LazyImage
                          width={100}
                          height="100%"
                          src={brand.thumbnailUrl}
                          contain={true}
                        />
                      </Button>
                    </TekoLink>
                  ) : null,
                )}
            </StyledFlex>
          );
        })}
      </>
    );
  };

  const renderOthersItem = (items: SubSectionItem[], label: string) => {
    return items.map((item, index) =>
      !!item.linkUrl && !!item.description ? (
        <TekoLink key={`${index}${item.description}`} href={item.linkUrl}>
          <Flex
            direction="column"
            border="1px solid border"
            borderRadius="s"
            padding="s4"
            justify="start"
            align="center"
            width={isNewLayoutDesktop ? '100%' : 110}
            height={isMobile ? 110 : 156}
            {...getTrackingData({
              isTrackContent: true,
              regionName: 'subSection',
              contentName: JSON.stringify({
                block: label,
                item: item.description,
              }),
              target: item.linkUrl,
            })}
          >
            <LazyImage
              src={item.thumbnailUrl || ''}
              height={isMobile ? 56 : 90}
              width={isMobile ? 56 : 90}
              imageWidth={isMobile ? 112 : 180}
              contain={true}
            />
            <StyleSubSectionWrapper
              style={{ marginTop: isNewLayoutDesktop ? '10px' : '4px' }}
            >
              <StyledAttribute isMobile={isMobile} newLayout={newLayout}>
                {item.description}
              </StyledAttribute>
            </StyleSubSectionWrapper>
          </Flex>
        </TekoLink>
      ) : (
        <></>
      ),
    );
  };

  const renderOthers = () => {
    if (!others || others.length === 0) return null;
    return (
      <>
        {others.map((block, index) => {
          const { title, items } = block;
          const itemsPerView = isMobile ? 3.45 : 8;
          if (!items?.length) {
            return null;
          }
          return (
            <Shape key={index} marginBottom="s4" marginTop="s12">
              <SubsectionHeader isMobile={isMobile} newLayout={newLayout}>
                {title}
              </SubsectionHeader>
              <StyledFlex
                gap={itemGap}
                marginTop={newLayout ? 's4' : 's8'}
                paddingBottom={isNewLayoutDesktop ? 's8' : undefined}
                fillItems={isNewLayoutDesktop}
                align="center"
                maxHeight={
                  isNewLayoutDesktop
                    ? undefined
                    : !items[0].thumbnailUrl
                    ? 88
                    : 156
                }
                flexWrap={isMobile ? 'inherit' : 'wrap'}
                style={{ overflow: isMobile ? 'scroll' : 'hidden' }}
              >
                {!!items[0].thumbnailUrl ? (
                  // add navigation arrows only if number of display items >= length of data
                  items.length >= itemsPerView ? (
                    <StyledGlide
                      perView={isNewLayoutDesktop ? 9.08 : itemsPerView}
                      gap={isNewLayoutMobile ? 12 : isNewLayoutDesktop ? 8 : 10}
                      breakpoints={
                        isNewLayoutDesktop
                          ? {
                              1599: { perView: 7.44 },
                              1399: { perView: 6.38 },
                            }
                          : {
                              410: { perView: 3.35 },
                              390: { perView: 3.15 },
                              370: { perView: 2.95 },
                              350: { perView: 2.75 },
                            }
                      }
                      type={isNewLayoutDesktop ? 'slider' : 'carousel'}
                      bound={isNewLayoutDesktop}
                      showControl
                      isMobile={isMobile}
                    >
                      {renderOthersItem(items, title || '')}
                    </StyledGlide>
                  ) : (
                    renderOthersItem(items, title || '')
                  )
                ) : (
                  <>
                    {items.map((item, index) =>
                      !!item.description && !!item.linkUrl ? (
                        <TekoLink key={index} href={item.linkUrl}>
                          <Button
                            padding={isMobile ? ['s0', 's12'] : ['s0', 's20']}
                            width={isMobile ? 'max-content' : '100%'}
                            variant="default"
                            {...getTrackingData({
                              isTrackContent: true,
                              regionName: 'subSection',
                              contentName: JSON.stringify({
                                block: title,
                                item: item.description,
                              }),
                              target: item.linkUrl,
                            })}
                          >
                            <StyledSubTitle
                              marginTop="s4"
                              weight="regular"
                              level={4}
                              line={1}
                            >
                              {item.description}
                            </StyledSubTitle>
                          </Button>
                        </TekoLink>
                      ) : null,
                    )}
                  </>
                )}
              </StyledFlex>
            </Shape>
          );
        })}
      </>
    );
  };

  return (
    <>
      {renderSuggestionBrands()}
      {renderOthers()}
    </>
  );
};

const StyleSubSectionWrapper = styled.div`
  display: flex;
  flex-grow: 1;
  align-items: center;
`;

const StyledSubTitle = styled(SubTitle)`
  text-align: center;
  flex-grow: 1;
  -webkit-box-align: center;
  -webkit-box-flex: 1;
  align-items: center;
`;

const StyledFlex = styled(Flex)<{ fillItems?: boolean }>`
  &::-webkit-scrollbar {
    display: none;
  }
  ${({ fillItems }) =>
    fillItems
      ? `
    [data-testid='TekoLink'] {
      display: block;
      width: 100%;
    }
  `
      : ''}
`;

const SubsectionHeader = styled.div<{ isMobile: boolean; newLayout?: boolean }>(
  ({ isMobile, newLayout }) => `
  margin: 0;
  font-size: ${isMobile || newLayout ? '13px' : '18px'};
  line-height: 24px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`,
);

const StyledAttribute = styled.div<{ isMobile: boolean; newLayout?: boolean }>(
  ({ isMobile, newLayout }) => `
  text-align: center;
  font-weight: 400;
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  ${
    isMobile
      ? `
  font-size: 12px;
  line-height: 16px;`
      : `
  flex-grow: 1;
  -webkit-box-align: center;
  -webkit-box-flex: 1;
  align-items: center;
  font-size: 13px;
  line-height: ${newLayout ? '20px' : '24px'};`
  }
`,
);

const StyledGlide = styled(Glide)(
  ({ isMobile }: { isMobile?: boolean }) => `
  ${
    isMobile
      ? `.glide__arrow--override {
    visibility: visible;
  }`
      : ''
  }
`,
);
